import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  TuneOutlined,
  PhotoCameraOutlined,
  CheckRounded,
  ArrowForwardRounded,
} from "@mui/icons-material";
import { adminAdjustBalance } from "api/wallet";
import { adjustBranchCash } from "api/financial";

const CURRENCIES = ["THB", "LAK", "USD"];
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const MONO = "'Space Grotesk', ui-monospace, monospace";
const money = (n, c = "THB") => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const initials = (s) => (s || "?").slice(0, 2).toUpperCase();

// Shared manual balance-adjustment dialog for both a customer wallet (mode="wallet")
// and a branch cash drawer (mode="branch"). Two input modes: enter the desired final
// balance ("By new balance", default) and the credit/debit is computed, OR enter the
// adjustment amount + direction directly. Both resolve to the same { amount, direction }
// payload — the target-balance maths is purely client-side.
export default function AdjustBalanceDialog({ mode = "wallet", open, onClose, onSuccess, balances = {}, target = {} }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const isBranch = mode === "branch";

  const [currency, setCurrency] = useState("THB");
  const [inputMode, setInputMode] = useState("byNewBalance");
  const [amount, setAmount] = useState("");      // byAmount
  const [direction, setDirection] = useState("add"); // byAmount
  const [newBalance, setNewBalance] = useState(""); // byNewBalance
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);        // branch only, optional
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const current = round2(balances[currency]);

  useEffect(() => {
    if (open) {
      setCurrency("THB");
      setInputMode("byNewBalance");
      setAmount("");
      setDirection("add");
      setNewBalance(String(round2(balances.THB)));
      setReason("");
      setFile(null);
      setError("");
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Switch currency + re-seed the target balance in the same update so they land in
  // one render together. Doing this via a separate effect keyed on `currency` left a
  // one-frame gap where `current` already reflected the new currency but `newBalance`
  // still held the old one — a bogus non-zero diff that flashed the credit/debit chip.
  const selectCurrency = (c) => {
    setCurrency(c);
    setNewBalance(String(round2(balances[c])));
  };

  // Resolve both input modes to a single { effAmount, effDir, after }.
  const resolved = useMemo(() => {
    if (inputMode === "byNewBalance") {
      const targetBal = round2(newBalance);
      const delta = round2(targetBal - current);
      return { effAmount: Math.abs(delta), effDir: delta >= 0 ? "add" : "deduct", after: targetBal };
    }
    const amt = round2(amount);
    const after = direction === "add" ? round2(current + amt) : round2(current - amt);
    return { effAmount: amt, effDir: direction, after };
  }, [inputMode, newBalance, amount, direction, current]);

  const { effAmount, effDir, after } = resolved;
  const isCredit = effDir === "add";
  // A drawer can never hold negative physical cash; block a branch deduct past zero.
  const overBalance = isBranch && !isCredit && effAmount > current;
  const noop = !effAmount || effAmount <= 0;
  const canSubmit = !noop && !overBalance && !!reason.trim() && !submitting;

  const handleSubmit = async () => {
    if (noop) return setError(t("financial.invalidAdjustment"));
    if (!reason.trim()) return setError(t("financial.reasonRequired"));
    if (overBalance) return setError(t("financial.overBalance"));
    setSubmitting(true);
    setError("");
    try {
      let res;
      if (isBranch) {
        const fd = new FormData();
        fd.append("stationId", target.id);
        fd.append("currency", currency);
        fd.append("amount", String(effAmount));
        fd.append("direction", effDir);
        fd.append("reason", reason.trim());
        if (file) fd.append("evidence_0", file);
        res = await adjustBranchCash(fd);
      } else {
        res = await adminAdjustBalance(target.id, effAmount, currency, effDir, reason.trim());
      }
      setSubmitting(false);
      if (res && res.success) onSuccess && onSuccess(res);
      else setError((res && res.message) || t("common.error"));
    } catch (e) {
      setSubmitting(false);
      setError(e?.message || t("common.error"));
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
              <TuneOutlined />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{t("financial.adjustTitle")}</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={submitting}><Close fontSize="small" /></IconButton>
        </Box>

        {/* who/which branch this adjustment applies to — avoids editing the wrong
            wallet or drawer when several dialogs/tabs are open at once. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 1.75, p: "10px 14px", borderRadius: "14px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg,#7c3aed,#4c1d95)", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {initials(target.name || target.id)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }} color="text.secondary">
              {isBranch ? t("financial.branchStation") : t("financial.customerAccount")}
            </Typography>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.3 }}>{target.name || target.id}</Typography>
            <Typography noWrap sx={{ fontFamily: MONO, fontSize: 11.5 }} color="text.secondary">
              {target.id}{target.phone ? ` · ${target.phone}` : ""}
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1.75} sx={{ mt: 1.75 }}>
          {/* currency tiles — pick which balance to adjust */}
          <Stack direction="row" spacing={1.25}>
            {CURRENCIES.map((c) => {
              const active = currency === c;
              return (
                <Box
                  key={c}
                  onClick={() => selectCurrency(c)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    border: active ? `1.5px solid ${accent}` : "1.5px solid #ece9f1",
                    background: active ? alpha(accent, 0.08) : "#fff",
                    borderRadius: "14px",
                    p: "10px 12px",
                    transition: "border-color .15s, background .15s",
                  }}
                >
                  <Typography noWrap sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", color: active ? accent : "text.secondary" }}>
                    {SYM[c]} {c}
                  </Typography>
                  {/* noWrap + fixed line-height: LAK balances run much longer than THB/USD —
                      without this a long number wraps to a 2nd line, growing this row and
                      shifting the centered dialog (looked like the whole modal "bouncing"). */}
                  <Typography noWrap sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 15, lineHeight: "20px", color: active ? accent : "text.primary", mt: "2px" }}>
                    {round2(balances[c]).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          {/* input-mode toggle */}
          <ToggleButtonGroup exclusive size="small" color="secondary" value={inputMode} onChange={(e, v) => v && setInputMode(v)} sx={{ width: "100%" }}>
            <ToggleButton value="byNewBalance" sx={{ flex: 1, textTransform: "none", fontWeight: 700 }}>{t("financial.byNewBalance")}</ToggleButton>
            <ToggleButton value="byAmount" sx={{ flex: 1, textTransform: "none", fontWeight: 700 }}>{t("financial.byAmount")}</ToggleButton>
          </ToggleButtonGroup>

          {/* inputs per mode */}
          {inputMode === "byNewBalance" ? (
            <TextField fullWidth size="small" type="number" color="secondary" label={t("financial.newBalance")} value={newBalance} onChange={(e) => setNewBalance(e.target.value)} />
          ) : (
            <Stack direction="row" spacing={1.5}>
              <TextField select size="small" color="secondary" label={t("financial.adjustType")} value={direction} onChange={(e) => setDirection(e.target.value)} sx={{ width: 190, flexShrink: 0 }}>
                <MenuItem value="add">{t("financial.add")}</MenuItem>
                <MenuItem value="deduct">{t("financial.deduct")}</MenuItem>
              </TextField>
              <TextField fullWidth size="small" type="number" color="secondary" label={t("financial.amount")} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Stack>
          )}

          {/* live preview: current → after + credit/debit chip */}
          <Box sx={{ px: 1.75, py: 1.5, borderRadius: "14px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11 }} color="text.secondary">{t("financial.currentBalance")}</Typography>
                <Typography noWrap sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 15, lineHeight: "20px" }}>{money(current, currency)}</Typography>
              </Box>
              <ArrowForwardRounded sx={{ color: "#b3aebd", flexShrink: 0 }} />
              <Box sx={{ textAlign: "right", minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11 }} color="text.secondary">{t("financial.afterBalance")}</Typography>
                <Typography noWrap sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 15, lineHeight: "20px", color: overBalance ? "error.main" : "text.primary" }}>{money(after, currency)}</Typography>
              </Box>
            </Box>
            {/* Always reserve this row's height (visibility, not conditional mount) so
                switching currency — which resets the target to the current balance,
                hiding the chip — doesn't collapse the card and jolt the content below. */}
            <Box sx={{ mt: 1.25, display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12.5, fontWeight: 700, px: 1.25, py: 0.5, borderRadius: "999px", color: isCredit ? "#15803d" : "#c0343a", background: isCredit ? "#eafaf0" : "#fdeef0", visibility: noop ? "hidden" : "visible" }}>
              {isCredit ? t("financial.willCredit") : t("financial.willDebit")} {money(effAmount || 0, currency)}
            </Box>
          </Box>

          {/* reason (required) */}
          <TextField fullWidth multiline rows={2} size="small" color="secondary" label={t("financial.adjustReason")} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("financial.adjustReasonPlaceholder")} />

          {/* optional evidence (branch only) */}
          {isBranch && (
            <Button variant={file ? "contained" : "outlined"} color="secondary" component="label" startIcon={file ? <CheckRounded /> : <PhotoCameraOutlined />} sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none", justifyContent: "flex-start", py: 1.25 }}>
              {file ? t("branchCash.slipAttached") : t("financial.attachPhotoOptional")}
              <input hidden accept="image/*" type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
            </Button>
          )}

          {overBalance && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{t("financial.overBalance")}</Alert>}
          {error && <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color={isCredit ? "success" : "error"} onClick={handleSubmit} disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined} sx={{ borderRadius: "12px", fontWeight: 700, px: 3 }}>
          {isCredit ? t("financial.willCredit") : t("financial.willDebit")} {currency}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
